/**
 * Данные мобильной версии на устройстве: обёртка над `localStorage`.
 *
 * Ключи и правила очистки — в specs/001-pwa-shell/contracts/device-storage.md.
 * Приватный режим Safari и заблокированные данные сайта делают `localStorage` недоступным
 * или запрещают запись: любое обращение здесь обёрнуто в try/catch, а чтение отдаёт
 * значение по умолчанию — приложение работает и без хранилища, просто ничего не помнит.
 */

/** Ключи `localStorage` */
export const storageKeys = {
    /** Выбор версии на устройстве: "mobile" | "full" (настройка устройства) */
    version: "rost.version",
    /** Выбор темы: "system" | "light" | "dark" (настройка устройства) */
    theme: "rost.theme",
    /** До какого момента предложение установки не показывается (ISO-дата) */
    installSnoozedUntil: "rost.install.snoozedUntil",
    /** "1" — выход без сети ещё не отправлен на сервер */
    logoutPending: "rost.logoutPending",
    /** Копия метаданных последнего успешного запуска вошедшего пользователя */
    metadata: "rost.m.metadata",
    /** Последняя вкладка ролей заданий: "executor" | "employer" | "author" (specs/004 FR-005) */
    tasksRole: "rost.m.tasks.role",
    /** Черновики комментариев и ответов заданий (specs/004 R9) */
    tasksDrafts: "rost.m.tasks.drafts",
    /** Положение «Моя штатка · Сотрудники»: "mine" | "all" (specs/005 R8; сумм не содержит) */
    staffView: "rost.m.staff.view",
    /** Копия списка запросов и согласований сдвигов для показа без сети (specs/007 R7) */
    requests: "rost.m.requests",
    /** Копия списка «На подпись» для показа без сети (specs/009 R7) */
    signatures: "rost.m.signatures",
    /** Вид календаря: "list" | "month" (specs/008 R4) */
    calendarView: "rost.m.calendar.view",
} as const;

/** Всё, что относится к пользователю, лежит под этим префиксом и удаляется при выходе */
export const USER_DATA_PREFIX = "rost.m.";

/** Хранилище или `null`: само обращение к `window.localStorage` в закрытом режиме может бросить */
function storage(): Storage | null {
    try {
        return typeof localStorage === "undefined" ? null : localStorage;
    } catch {
        return null;
    }
}

/** Прочитать значение; нет ключа или хранилище недоступно — `fallback` */
export function readValue<T extends string | null>(key: string, fallback: T): string | T {
    try {
        const value = storage()?.getItem(key);

        return value === null || value === undefined ? fallback : value;
    } catch {
        return fallback;
    }
}

/** Записать значение; `false`, если хранилище недоступно или полно */
export function writeValue(key: string, value: string): boolean {
    try {
        const target = storage();

        if (!target) {
            return false;
        }

        target.setItem(key, value);

        return true;
    } catch {
        return false;
    }
}

/** Удалить ключ */
export function removeValue(key: string): void {
    try {
        storage()?.removeItem(key);
    } catch {
        // нечего удалять — хранилища нет
    }
}

/** Прочитать JSON; повреждённое значение считается отсутствующим */
export function readJson<T>(key: string): T | null {
    const raw = readValue(key, null);

    if (raw === null) {
        return null;
    }

    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

/** Записать JSON */
export function writeJson(key: string, value: unknown): boolean {
    try {
        return writeValue(key, JSON.stringify(value));
    } catch {
        return false;
    }
}

/**
 * Копия метаданных для запуска без сети (research R7).
 * Подмножество `AppMetadata` последнего успешного запуска вошедшего пользователя.
 */
export interface MetadataCopy {
    version: string;
    userId: string;
    userLogin: string;
    userName: string;
    userAvatar: string | null;
    access: string[];
    startInfoFilled: boolean;
    constants: any;
    /** Когда копия сделана, мс с начала эпохи */
    savedAt: number;
}

/**
 * Константы метаданных без секретов сервера. Сервер отдаёт в них закрытый ключ push
 * (`pushPrivateKey`) — клиенту он не нужен, и на устройстве ему не место.
 */
export function deviceConstants(constants: any): Record<string, any> | null {
    if (!constants || typeof constants !== "object") {
        return null;
    }

    const { pushPrivateKey, ...rest } = constants;

    return rest;
}

/** Сохранить копию метаданных после успешной загрузки вошедшего пользователя */
export function saveMetadataCopy(meta: AppMetadata, now: number = Date.now()): boolean {
    const copy: MetadataCopy = {
        version: meta.version,
        userId: meta.userId,
        userLogin: meta.userLogin,
        userName: meta.userName,
        userAvatar: meta.userAvatar ?? null,
        access: [...(meta.access || [])],
        startInfoFilled: meta.startInfoFilled,
        constants: deviceConstants(meta.constants),
        savedAt: now,
    };

    return writeJson(storageKeys.metadata, copy);
}

/** Копия метаданных; нет копии или она повреждена — `null` */
export function loadMetadataCopy(): MetadataCopy | null {
    const copy = readJson<Partial<MetadataCopy>>(storageKeys.metadata);

    // без пользователя копия не нужна: она и есть признак «вход был, выхода не было»
    if (!copy || typeof copy.userId !== "string" || !copy.userId || !Array.isArray(copy.access)) {
        return null;
    }

    return {
        version: copy.version ?? "",
        userId: copy.userId,
        userLogin: copy.userLogin ?? "",
        userName: copy.userName ?? "",
        userAvatar: copy.userAvatar ?? null,
        access: copy.access,
        startInfoFilled: copy.startInfoFilled ?? true,
        constants: deviceConstants(copy.constants),
        savedAt: copy.savedAt ?? 0,
    };
}

/** Удалить копию метаданных (ответ 401, выход) */
export function clearMetadataCopy(): void {
    removeValue(storageKeys.metadata);
}

/** Есть ли неотправленный на сервер выход */
export function isLogoutPending(): boolean {
    return readValue(storageKeys.logoutPending, "") === "1";
}

/** Поставить или снять отметку «выход ещё не отправлен» */
export function setLogoutPending(pending: boolean): void {
    if (pending) {
        writeValue(storageKeys.logoutPending, "1");
    } else {
        removeValue(storageKeys.logoutPending);
    }
}

/**
 * Удалить данные пользователя (`rost.m.*`) при выходе.
 * Настройки устройства (версия, тема, «Позже») и отметка `logoutPending` остаются.
 */
export function clearUserData(): void {
    try {
        const target = storage();

        if (!target) {
            return;
        }

        // ключи собираем заранее: удаление в процессе обхода сдвигает индексы
        const keys: string[] = [];

        for (let i = 0; i < target.length; i++) {
            const key = target.key(i);

            if (key && key.startsWith(USER_DATA_PREFIX)) {
                keys.push(key);
            }
        }

        keys.forEach((key) => target.removeItem(key));
    } catch {
        // хранилище недоступно — удалять нечего
    }
}
